import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { AppContext } from '../../application/provider';

// ─── Catálogos ───────────────────────────────────────────────────────────────
const TIPOS_COMPROBANTE = [
    { value: 1, label: 'Factura' },
    { value: 2, label: 'Boleta' },
    { value: 3, label: 'Recibo por Honorarios' },
    { value: 4, label: 'Nota de Crédito' },
    { value: 5, label: 'Liquidación' },
];

const TIPOS_BIEN_SERVICIO = [
    { value: 1, label: 'Servicio' },
    { value: 2, label: 'Producto' },
];

const ESTADOS_PROCESO = [
    { value: 0, label: 'Solicitado' },
    { value: 1, label: 'En Revisión' },
    { value: 2, label: 'Aprobado' },
    { value: 3, label: 'Rechazado' },
    { value: 4, label: 'En Proceso de Pago' },
    { value: 5, label: 'Pagado Parcial' },
    { value: 6, label: 'Pagado Total' },
    { value: 7, label: 'Anulado' },
];

const ESTADOS_PAGO = [
    { value: 0, label: 'Pendiente' },
    { value: 1, label: 'Pago Parcial' },
    { value: 2, label: 'Pagado' },
];

const ESTADOS_CONTABILIDAD = [
    { value: 0, label: 'Pendiente Revisión' },
    { value: 1, label: 'Revisado' },
    { value: 2, label: 'Contabilizado' },
];

// ─── Initial state ────────────────────────────────────────────────────────────
const initialForm = {
    empresa_id: '',
    proveedor: '',
    fecha_solicitud: '',
    tipo_comprobante: '',
    serie_comprobante: '',
    numero_comprobante: '',
    tipo_bien_servicio: '',
    monto_total: '',
    estado_proceso: 0,
    estado_pago: 0,
    entidad_financiera_id: '',
    fecha_pago: '',
    numero_operacion: '',
    estado_contabilidad: 0,
    fecha_registro_contable: '',
    observacion: '',
};

// ─── Component ────────────────────────────────────────────────────────────────
const ModalFinanzasPago = ({ isOpen, onClose, pago, onSave }) => {
    const [state] = useContext(AppContext);
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const isEditing = !!pago;

    useEffect(() => {
        if (isOpen) {
            if (pago) {
                setForm({
                    empresa_id: pago.empresa_id ?? '',
                    proveedor: pago.proveedor ?? '',
                    fecha_solicitud: pago.fecha_solicitud ?? '',
                    tipo_comprobante: pago.tipo_comprobante ?? '',
                    serie_comprobante: pago.serie_comprobante ?? '',
                    numero_comprobante: pago.numero_comprobante ?? '',
                    tipo_bien_servicio: pago.tipo_bien_servicio ?? '',
                    monto_total: pago.monto_total ?? '',
                    estado_proceso: pago.estado_proceso ?? 0,
                    estado_pago: pago.estado_pago ?? 0,
                    entidad_financiera_id: pago.entidad_financiera_id ?? '',
                    fecha_pago: pago.fecha_pago ?? '',
                    numero_operacion: pago.numero_operacion ?? '',
                    estado_contabilidad: pago.estado_contabilidad ?? 0,
                    fecha_registro_contable: pago.fecha_registro_contable ?? '',
                    observacion: pago.observacion ?? '',
                });
            } else {
                setForm(initialForm);
            }
        }
    }, [isOpen, pago]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.empresa_id || !form.proveedor.trim() || !form.fecha_solicitud ||
            !form.tipo_bien_servicio || !form.monto_total) {
            alert('Complete los campos obligatorios (*).');
            return;
        }

        const currentPerfilId = Number(state.user?.perfil_id || state.perfil_id);
        const payload = {
            ...form,
            perfil_id: currentPerfilId,
            usuario_id: state.user?.usuario_id || state.usuario_id,
            proveedor: form.proveedor.toUpperCase(),
        };

        if (isEditing) {
            payload.pago_id = pago.pago_id;
        }

        setSaving(true);
        try {
            await onSave(payload, isEditing);
            onClose();
        } catch (err) {
            alert(err?.response?.data?.message || 'Error al guardar el pago.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="lg">
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>{isEditing ? 'EDITAR PAGO' : 'NUEVO PAGO'}</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body">
                    <Form className="fila" onSubmit={e => e.preventDefault()}>

                        {/* Empresa */}
                        <FormGroup>
                            <Label>Empresa ID: *</Label>
                            <Input
                                className="form-control"
                                type="number"
                                name="empresa_id"
                                value={form.empresa_id}
                                onChange={handleChange}
                                placeholder="ID de empresa"
                            />
                        </FormGroup>

                        {/* Proveedor */}
                        <FormGroup>
                            <Label>Proveedor: *</Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="proveedor"
                                value={form.proveedor}
                                onChange={handleChange}
                                placeholder="Nombre del proveedor"
                                maxLength={150}
                            />
                        </FormGroup>

                        {/* Fecha Solicitud */}
                        <FormGroup>
                            <Label>Fecha de Solicitud: *</Label>
                            <Input
                                className="form-control"
                                type="date"
                                name="fecha_solicitud"
                                value={form.fecha_solicitud}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        {/* Comprobante */}
                        <FormGroup>
                            <Label>Tipo de Comprobante:</Label>
                            <Input
                                className="form-control"
                                type="select"
                                name="tipo_comprobante"
                                value={form.tipo_comprobante}
                                onChange={handleChange}
                            >
                                <option value="">— Seleccione —</option>
                                {TIPOS_COMPROBANTE.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label>Serie Comprobante:</Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="serie_comprobante"
                                value={form.serie_comprobante}
                                onChange={handleChange}
                                placeholder="Ej: F001"
                                maxLength={10}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Número de Comprobante:</Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="numero_comprobante"
                                value={form.numero_comprobante}
                                onChange={handleChange}
                                placeholder="Ej: 00001234"
                                maxLength={20}
                            />
                        </FormGroup>

                        {/* Tipo Bien / Servicio */}
                        <FormGroup>
                            <Label>Tipo Bien / Servicio: *</Label>
                            <Input
                                className="form-control"
                                type="select"
                                name="tipo_bien_servicio"
                                value={form.tipo_bien_servicio}
                                onChange={handleChange}
                            >
                                <option value="">— Seleccione —</option>
                                {TIPOS_BIEN_SERVICIO.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        {/* Monto */}
                        <FormGroup>
                            <Label>Monto Total: *</Label>
                            <Input
                                className="form-control"
                                type="number"
                                name="monto_total"
                                value={form.monto_total}
                                onChange={handleChange}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                            />
                        </FormGroup>

                        {/* Campos solo en edición */}
                        {isEditing && (
                            <>
                                <FormGroup>
                                    <Label>Estado Proceso:</Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        name="estado_proceso"
                                        value={form.estado_proceso}
                                        onChange={handleChange}
                                    >
                                        {ESTADOS_PROCESO.map(e => (
                                            <option key={e.value} value={e.value}>{e.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label>Estado de Pago:</Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        name="estado_pago"
                                        value={form.estado_pago}
                                        onChange={handleChange}
                                    >
                                        {ESTADOS_PAGO.map(e => (
                                            <option key={e.value} value={e.value}>{e.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label>Entidad Financiera ID:</Label>
                                    <Input
                                        className="form-control"
                                        type="number"
                                        name="entidad_financiera_id"
                                        value={form.entidad_financiera_id}
                                        onChange={handleChange}
                                        placeholder="ID entidad financiera"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>Fecha de Pago:</Label>
                                    <Input
                                        className="form-control"
                                        type="date"
                                        name="fecha_pago"
                                        value={form.fecha_pago}
                                        onChange={handleChange}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>Número de Operación:</Label>
                                    <Input
                                        className="form-control"
                                        type="text"
                                        name="numero_operacion"
                                        value={form.numero_operacion}
                                        onChange={handleChange}
                                        placeholder="Número de operación bancaria"
                                        maxLength={50}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>Estado Contabilidad:</Label>
                                    <Input
                                        className="form-control"
                                        type="select"
                                        name="estado_contabilidad"
                                        value={form.estado_contabilidad}
                                        onChange={handleChange}
                                    >
                                        {ESTADOS_CONTABILIDAD.map(e => (
                                            <option key={e.value} value={e.value}>{e.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label>Fecha Registro Contable:</Label>
                                    <Input
                                        className="form-control"
                                        type="date"
                                        name="fecha_registro_contable"
                                        value={form.fecha_registro_contable}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </>
                        )}

                        {/* Observación */}
                        <FormGroup>
                            <Label>Observación:</Label>
                            <Input
                                className="form-control"
                                type="textarea"
                                name="observacion"
                                rows={3}
                                value={form.observacion}
                                onChange={handleChange}
                                placeholder="Observaciones adicionales..."
                            />
                        </FormGroup>

                        {/* Botón */}
                        <FormGroup style={{ marginTop: '18px' }}>
                            <div className="button-container">
                                <Button
                                    className="button-custom"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                >
                                    {saving ? 'REGISTRANDO...' : 'REGISTRAR'}
                                </Button>
                            </div>
                        </FormGroup>

                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalFinanzasPago;